import React, { useState } from 'react';
import { Modal, Upload, Button, Alert, Table, Typography, Space, Divider } from 'antd';
import { UploadOutlined, CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import Papa from 'papaparse';
import styled from 'styled-components';
import axios from 'axios';
import { URLs } from '../../../store/urls';
import { notificationController } from '../../../controllers/notificationController';

const { Text } = Typography;

// Styled components
const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
  }
  
  .ant-modal-header {
    border-radius: 12px 12px 0 0;
  }
`;

const UploadArea = styled.div`
  padding: 24px;
  background: #fafafa;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  text-align: center;
  transition: all 0.3s;
  
  &:hover {
    border-color: #1677ff;
    background: #f0f5ff;
  }
`;

const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 8px 0 0 0;
  max-height: 150px;
  overflow-y: auto;
  
  li {
    padding: 4px 0;
    color: #ff4d4f;
    font-size: 13px;
  }
`;

const SuccessMessage = styled.div`
  padding: 12px 16px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
  margin-top: 16px;
`;

const InstructionsBox = styled.div`
  padding: 12px 16px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 8px;
  margin-bottom: 16px;
  
  .instruction-title {
    font-weight: 600;
    margin-bottom: 6px;
    color: #0958d9;
    font-size: 13px;
  }
  
  .instruction-list {
    margin: 0;
    padding-left: 20px;
    font-size: 12px;
    
    li {
      margin: 3px 0;
      color: #434343;
    }
  }
`;

// Required columns for CSV
const REQUIRED_COLUMNS = ['Name', 'Email', 'Phone', 'DateOfBirth'];

interface ParsedStudent {
  name: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
}

interface ImportResult {
  imported: number;
  failed: number;
  errors?: { row: number; error: string }[];
}

interface UploadStudentsCsvProps {
  visible: boolean;
  onClose: () => void;
  onImport: (students: any[]) => void;
  classId: number;
}

const UploadStudentsCsv: React.FC<UploadStudentsCsvProps> = ({
  visible,
  onClose,
  onImport,
  classId
}) => {
  const [errors, setErrors] = useState<string[]>([]);
  const [students, setStudents] = useState<ParsedStudent[]>([]);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Reset state when modal closes
  const handleClose = () => {
    setErrors([]);
    setStudents([]);
    setFileUploaded(false);
    setSelectedFile(null);
    onClose();
  };

  // Download CSV template
  const downloadTemplate = () => {
    const csvContent = 'Name,Email,Phone,DateOfBirth\nJohn Doe,john.doe@example.com,+12345678901,2000-01-15\nJane Smith,jane.smith@example.com,+19876543210,1999-05-20';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'students_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Validate name (2 words, min 2 chars each)
  const isValidName = (name: string): boolean => {
    const words = name.trim().split(/\s+/);
    return words.length >= 2 && words[0].length >= 2 && words.slice(1).join(' ').length >= 2;
  };

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate phone format (+[digits], min 10 digits)
  const isValidPhone = (phone: string): boolean => {
    const cleaned = phone.replace(/\s/g, '');
    // Must start with + followed by digits only
    const phoneRegex = /^\+\d+$/;
    if (!phoneRegex.test(cleaned)) return false;
    // Must have at least 10 digits
    const digitsOnly = cleaned.replace(/\D/g, '');
    return digitsOnly.length >= 10;
  };

  // Validate date format (YYYY-MM-DD)
  const isValidDate = (date: string): boolean => {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) return false;
    const parsedDate = new Date(date);
    return parsedDate instanceof Date && !isNaN(parsedDate.getTime());
  };

  // Handle file upload (preview validation)
  const handleUpload = (file: File) => {
    setLoading(true);
    setErrors([]);
    setStudents([]);
    setFileUploaded(false);
    setSelectedFile(file);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as any[];
        const fileColumns = results.meta.fields || [];
        const foundErrors: string[] = [];
        const validStudents: ParsedStudent[] = [];

        // Check for Name column (required)
        if (!fileColumns.includes('Name')) {
          foundErrors.push('❌ Missing required column: "Name"');
        }

        if (foundErrors.length > 0) {
          setErrors(foundErrors);
          setLoading(false);
          setSelectedFile(null);
          return;
        }

        // Validate each row
        data.forEach((row, index) => {
          const rowNumber = index + 2;
          
          const allEmpty = Object.values(row).every((v) => v === '' || v == null);
          if (allEmpty) return;

          // Check name
          if (!row.Name || row.Name.trim() === '') {
            foundErrors.push(`❌ Line ${rowNumber}: Name is required`);
          } else if (!isValidName(row.Name)) {
            foundErrors.push(`⚠️ Line ${rowNumber}: Name should be "First Last" (2+ chars each)`);
          }

          // Validate email if provided
          if (row.Email && row.Email.trim() !== '' && !isValidEmail(row.Email)) {
            foundErrors.push(`⚠️ Line ${rowNumber}: Invalid email "${row.Email}"`);
          }

          // Validate phone if provided
          if (row.Phone && row.Phone.trim() !== '' && !isValidPhone(row.Phone)) {
            foundErrors.push(`⚠️ Line ${rowNumber}: Phone must start with + followed by digits (e.g. +12345678901)`);
          }

          // Validate date if provided
          if (row.DateOfBirth && row.DateOfBirth.trim() !== '' && !isValidDate(row.DateOfBirth)) {
            foundErrors.push(`⚠️ Line ${rowNumber}: Date must be YYYY-MM-DD`);
          }

          // Add to preview
          if (row.Name && row.Name.trim()) {
            validStudents.push({
              name: row.Name.trim(),
              email: row.Email?.trim() || undefined,
              phone: row.Phone?.trim() || undefined,
              dateOfBirth: row.DateOfBirth?.trim() || undefined
            });
          }
        });

        // Any errors or warnings block import
        if (foundErrors.length > 0) {
          setErrors(foundErrors);
          setStudents(validStudents); // Show preview but don't allow import
          setFileUploaded(false);
          setSelectedFile(null);
        } else {
          setStudents(validStudents);
          setFileUploaded(true);
          setErrors([]);
        }
        
        setLoading(false);
      },
      error: (err) => {
        setErrors([`❌ Failed to parse CSV: ${err.message}`]);
        setLoading(false);
        setSelectedFile(null);
      }
    });

    return false;
  };

  // Handle import - send file to server
  const handleImportConfirm = async () => {
    if (!selectedFile || !classId) {
      console.error('[CSV Import] Missing file or classId', { selectedFile, classId });
      return;
    }

    setImporting(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const url = `${URLs.BASE_URL}${URLs.IMPORT_STUDENTS_CSV_ENDPOINT}?classId=${classId}`;

      const response = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || ''}`
        }
      });


      // Handle different response formats
      const data = response.data;
      const isSuccess = data.success === true;
      const result: ImportResult = data.data || data;

      // Check result - could be partial success
      const importedCount = result.imported ?? 0;
      const failedCount = result.failed ?? 0;
      const serverErrors = result.errors || [];

      if (isSuccess || importedCount > 0) {
        // Full or partial success
        if (failedCount > 0) {
          notificationController.warning({
            message: 'Partial Import',
            description: `${importedCount} imported, ${failedCount} failed`
          });
          if (serverErrors.length > 0) {
            setErrors(serverErrors.map((e: any) => 
              `❌ Row ${e.row}: ${e.error}`
            ));
          }
        } else {
          notificationController.success({
            message: 'Import Successful',
            description: `${importedCount} student${importedCount !== 1 ? 's' : ''} imported`
          });
          onImport(students);
          handleClose();
        }
        
        // Refresh list if any students were imported
        if (importedCount > 0) {
          onImport(students);
        }
      } else {
        // Complete failure
        console.error('[CSV Import] Server returned failure', { data });
        
        if (serverErrors.length > 0) {
          setErrors(serverErrors.map((e: any) => 
            `❌ Row ${e.row}: ${e.error}`
          ));
          notificationController.error({
            message: 'Import Failed',
            description: `All ${failedCount} records failed. Check errors below.`
          });
        } else {
          throw new Error('Import failed - no details provided');
        }
      }
    } catch (error: any) {
      console.error('[CSV Import] Error:', error);
      console.error('[CSV Import] Error response:', error.response);
      console.error('[CSV Import] Error response data:', error.response?.data);
      
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.message 
        || error.message 
        || 'Failed to import students';
      const serverErrors = error.response?.data?.data?.errors || error.response?.data?.errors || [];
      
      if (serverErrors.length > 0) {
        setErrors(serverErrors.map((e: any) => `❌ Row ${e.row}: ${e.error}`));
      }
      
      notificationController.error({
        message: 'Import Failed',
        description: errorMessage
      });
    } finally {
      setImporting(false);
    }
  };

  // Upload props
  const uploadProps: UploadProps = {
    accept: '.csv',
    beforeUpload: handleUpload,
    showUploadList: false,
    multiple: false
  };

  // Table columns for preview
  const previewColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', width: 150 },
    { title: 'Email', dataIndex: 'email', key: 'email', render: (v: string) => v || '—' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', render: (v: string) => v || '—' },
    { title: 'Date of Birth', dataIndex: 'dateOfBirth', key: 'dateOfBirth', render: (v: string) => v || '—' }
  ];

  return (
    <StyledModal
      title="Import Students from CSV"
      open={visible}
      onCancel={handleClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={importing}>
          Cancel
        </Button>,
        <Button
          key="import"
          type="primary"
          onClick={handleImportConfirm}
          disabled={!fileUploaded || students.length === 0 || errors.length > 0 || importing}
          icon={importing ? <LoadingOutlined /> : <CheckCircleOutlined />}
          loading={importing}
        >
          {importing ? 'Importing...' : `Import ${students.length > 0 ? `${students.length} Students` : ''}`}
        </Button>
      ]}
    >
      <InstructionsBox>
        <div className="instruction-title">📋 CSV Format:</div>
        <ul className="instruction-list">
          <li><strong>Required:</strong> Name (First Last)</li>
          <li><strong>Optional:</strong> Email, Phone (+digits), DateOfBirth (YYYY-MM-DD)</li>
          <li><strong>Phone format:</strong> +12345678901 (starts with +, then digits only)</li>
        </ul>
        <Button 
          icon={<DownloadOutlined />} 
          onClick={downloadTemplate}
          size="small"
          style={{ marginTop: 8 }}
        >
          Download Template
        </Button>
      </InstructionsBox>

      <UploadArea>
        <Upload {...uploadProps}>
          <Button 
            icon={<UploadOutlined />} 
            size="large" 
            loading={loading}
            type="primary"
          >
            {loading ? 'Parsing...' : 'Select CSV File'}
          </Button>
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            Click to upload or drag and drop
          </Text>
        </Upload>
      </UploadArea>

      {errors.length > 0 && (
        <Alert
          message={errors.some(e => e.startsWith('❌')) ? 'Errors Found' : 'Warnings'}
          description={
            <ErrorList>
              {errors.slice(0, 10).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
              {errors.length > 10 && <li>...and {errors.length - 10} more</li>}
            </ErrorList>
          }
          type={errors.some(e => e.startsWith('❌')) ? 'error' : 'warning'}
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      {students.length > 0 && (
        <>
          {fileUploaded && errors.length === 0 ? (
            <SuccessMessage>
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                <Text strong style={{ color: '#52c41a', fontSize: 13 }}>
                  {students.length} student{students.length > 1 ? 's' : ''} ready to import
                </Text>
              </Space>
            </SuccessMessage>
          ) : (
            <Alert
              message="Fix errors above to enable import"
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          <Divider style={{ margin: '12px 0' }}>Preview ({students.length} records)</Divider>

          <Table
            columns={previewColumns}
            dataSource={students.map((s, idx) => ({ ...s, key: idx }))}
            pagination={{ pageSize: 5, size: 'small' }}
            size="small"
            scroll={{ x: 500 }}
          />
        </>
      )}
    </StyledModal>
  );
};

export default UploadStudentsCsv;
